import React, { useMemo, useState, useEffect } from "react";
import DOMPurify from "dompurify";
import {
  FormHelperText,
  Stack,
  TableCell,
  TableRow,
  Typography,
  Tooltip,
  Box,
  CircularProgress,
  IconButton,
  Button,
  Select,
  MenuItem,
  FormControl,
  SelectChangeEvent,
} from "@mui/material";
import { processesApi } from "api";
import Labels from "./labels/Labels";
import {
  formatDate,
  formatDuration,
  getFirstSentence,
  getLabelDataFromJsonTags,
  getTranslation,
  getUserData,
  getUserDataValue,
  shouldTruncate,
} from "common/utilities";
import { DeleteForever, Edit as EditIcon, ExpandMore } from "@mui/icons-material";
import { useSelectedOrganization, useSnackbar, useTranslations } from "hooks";
import { Priority } from "hooks/usePriorities";
// import { TaskDetailDialog } from "./dialogs";
import { StartedProcess } from "./ProcessItem";
import { Link, useNavigate } from "react-router-dom";
import { BaseTask } from "model/activitiesModel";
import BlockIcon from "@mui/icons-material/Block";
import { ClockIcon } from "@mui/x-date-pickers";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
// import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
// import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { Monitor } from "hooks/useMonitor";
import SubstitutesMenu, { SubstituteUser } from "./menus/SubstitutesMenu";
import PriorityMenu from "./menus/PriorityMenu";
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { flownodesApi } from "api";
import MonitorParticipants from "./MonitorParticipants";
export interface TaskDetail extends BaseTask {
  designerid: string;
  user_picture: string | null;
  process_description: string;
  descriptiondetail?: string | null;
  process_enddate: string | null;
  process_idprocessmodel: number;
  process_startdate: string | null;
  process_userdata: string;
  processduration: number;
  processmodeldeleted: boolean;
  flownodetype: number;
  idtype: number;
  iduser: number;
  previous: number;
  start_date?: string | null;
  taskduration: number | null;
  timeindoing: number;
  timetotakeover: number;
  userfullname: string | null;
  jsontag: string | null;
  jsongroups: string | null;
  jsonroles: string | null;
  priorityname: string | null;
  priorityorder: number | null;
}
interface TaskItemProps {
  handleClick: (
    task: TaskDetail,
    type: "delete" | "terminate" | "disclose" | "reschedule"
  ) => void;
  task: TaskDetail;
  process: StartedProcess;
  filters: Record<string, any>;
  setSelectedTask: (task: BaseTask) => void;
  userDataKeys: string[];
  monitors: Monitor[];
  isMonitorsLoading: boolean;
  // expandedCells: Record<string, boolean>;
  // toggleExpand: (key: string, e: React.MouseEvent) => void;
  openFullContentDialog: (content: string, title: string) => void;
  refresh: () => void;
  allPriorities: Priority[];
  loadingPriorities: boolean;
  history: boolean;
  onExpandProcess?: () => void;
}

const TaskItem: React.FC<TaskItemProps> = React.memo(({
  handleClick,
  filters,
  task,
  process,
  setSelectedTask,
  userDataKeys,
  monitors,
  isMonitorsLoading,
  // expandedCells,
  // toggleExpand,
  openFullContentDialog,
  refresh,
  allPriorities,
  loadingPriorities,
  history,
  onExpandProcess
}) => {
  // const selectedOrganization = useSelectedOrganization();
  // const [user] = useState<StatisticUser | null>(null);
  const translations = useTranslations();
  const navigation = useNavigate()
  const { sendSnack } = useSnackbar();

  const [localPriorityName, setLocalPriorityName] = useState<string | null>(task.priorityname);
  const [isEditingPriority, setIsEditingPriority] = useState(false);

  const [localUser, setLocalUser] = useState({
    iduser: task.iduser,
    userfullname: task.userfullname,
    user_picture: task.user_picture
  });

  const activeTimers = useMemo(
    () =>
      monitors
        .filter((timer) => {
          return timer.idprocesstoken === task.idprocesstoken;
        })
        .map((timer) => timer.starttime),
    [monitors, task.idprocesstoken]
  );

  // useEffect(() => {
  //   (async () => {
  //     if (task.iduser) {
  //       const { data } = await usersApi.get(`${task.iduser}`);
  //       setUser(data.user);
  //     }
  //   })();
  // }, [task.iduser]);

  const userDataMap = useMemo(() => {
    try {
      const data = getUserData(task.process_userdata);
      return data;
    } catch (e) {
      return {};
    }
  }, [task.process_userdata]);

  // State for participant edit mode

  // State for participant edit mode
  const [isEditingParticipant, setIsEditingParticipant] = useState(false);

  // const { sendSnack } = useSnackbar();

  // const handleEarlyTermination = async (task: TaskDetail) => {
  //   try {
  //     const { data } = await processesApi.post(`/${task.idprocesstoken}/intermediateevents/terminate`);
  //     sendSnack({ message: data.responsemessage, type: "success" });
  //   } catch { }
  // };

  const handlePriorityUpdate = async (newPriority: Priority) => {
    if (newPriority) {
      const oldPriorityName = localPriorityName;
      setLocalPriorityName(String(newPriority.name));
      setIsEditingPriority(false);

      try {
        const { data } = await flownodesApi.put(`/${task.idprocess}/priorities/${newPriority.id}`, {});
        sendSnack({ type: "success", message: data.responsemessage });
      } catch (error) {
        setLocalPriorityName(oldPriorityName);
        sendSnack({ type: "error", message: getTranslation(translations, "generic.error") });
      }
    }
  };

  const handleParticipantUpdate = async (newUser: SubstituteUser | null) => {
    if (newUser) {
      const oldUser = { ...localUser };
      setLocalUser({
        iduser: newUser.id,
        userfullname: `${newUser.firstname} ${newUser.lastname}`,
        user_picture: newUser.picture || null
      });
      setIsEditingParticipant(false);

      try {
        await processesApi.put(`/tokens/${task.idprocesstoken}`, {
          assigned_to: newUser.id,
        });
        sendSnack({
          message: getTranslation(translations, "generic.success"),
          type: "success",
        });
      } catch (error) {
        setLocalUser(oldUser);
        sendSnack({
          message: getTranslation(translations, "generic.error"),
          type: "error",
        });
      }
    }
  };

  const fixHeight = {
    // maxHeight: 130,
    maxHeight: "100px",
    // minHeight: 130,
    // boxSizing: "border-box",
    overflowY: "auto",
  };
  const [isHovered, setIsHovered] = useState(false);
  const selectedOrganization = useSelectedOrganization();
  const isAdmin = selectedOrganization?.administrator;

  return (
    <React.Fragment>
      <TableRow
        id={`task-row-${process.process_idprocessmodel}`}

        sx={{
          "& > td": {
            p: 2,
            lineHeight: 1,
            ...fixHeight,
          },
          '&:last-child td, &:last-child th': {
            borderBottom: 'none'
          },
          cursor: "pointer",
          "&:hover": { backgroundColor: (_) => _.palette.divider },
        }}
        onClick={(e) => {
          const selection = window.getSelection();
          if (selection && selection.toString()) return;
          setSelectedTask(task);
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <TableCell
          id="process-header-rows"
          sx={{
            // py: 0,
            opacity: 1,
            position: "sticky",
            left: 0,
            zIndex: 10,
            borderLeft: "1px solid #ddd",
            backgroundColor: isHovered ? "#e0e0e0" : "white"
          }}
        >
          <Stack>
            <Stack
              // alignItems="center"
              spacing={0.5}
              minWidth="400px"
              // flex={1}
              // width="auto"
              direction="row"
              // maxWidth="150px"
              display="flex"
              flexDirection="column"
            >
              {/* <Link
                onClick={(e) => void e.stopPropagation()}
                to={`/dashboard/process/edit/${process.process_idprocessmodel}`}
                style={{
                  fontWeight: "bold",
                  textDecoration: "none",
                  color: "black "
                }}
                state={{ designerid: task.designerid }}> */}
              <span style={{ marginBottom: 8 }}>
                <Labels
                  labelCompleteMode
                  inCard
                  labels={getLabelDataFromJsonTags(task?.jsontag)}
                />
              </span>
              <span
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  textDecoration: "none",
                  fontWeight: "bold",
                  flex: 1,
                  paddingBottom: 8,
                  color: "black",
                  lineHeight: 1.2
                }}
              >
                {process.process_description}
              </span>
              <span>
                <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                  <AccountTreeIcon
                    sx={{
                      verticalAlign: "middle",
                      color: "primary.main",
                      cursor: "pointer",
                      ":hover": { color: "secondary.main" }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigation(`/dashboard/process/edit/${process.process_idprocessmodel}`, {
                        state: { designerid: task.designerid }
                      });
                    }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {process.processmodelname || "no name"}
                  </Typography>
                </Stack>
                <FormHelperText>
                  {getTranslation(translations, "pm.process.started_at")}
                  {formatDate(process.process_startdate)}
                </FormHelperText>
              </span>
              {/* <ArrowForwardSharp fontSize={"small"} />
              <Link
                onClick={(e) => void e.stopPropagation()}
                to={`/dashboard/process/edit/${process.process_idprocessmodel}`}
                style={{ fontWeight: "bold" }}
                state={{ designerid: task.designerid }}
              >
                {`${task.processmodelname}`}
              </Link> */}
              {/* </Link> */}
            </Stack>
          </Stack>
        </TableCell>
        {/* <TableCell> */}
        {/* <ArrowForwardSharp fontSize={"small"} /> */}
        {/* <Box
            sx={{ maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis" }}
          >
            <Link
              onClick={(e) => void e.stopPropagation()}
              to={`/dashboard/process/edit/${process.process_idprocessmodel}`}
              style={{
                fontWeight: "bold",
              }}
              state={{ designerid: task.designerid }}
            >
              {task.processmodelname}
            </Link>
          </Box> */}
        {/* </TableCell> */}
        {userDataKeys.map((key) => {
          const value = userDataMap[key];
          // const cellKey = `${task.idprocesstoken}-${key}`;
          // const isExpanded = expandedCells[cellKey];
          const needsTruncation = value && shouldTruncate(getUserDataValue(value));
          // console.log({ cellKey, isExpanded, needsTruncation, value });

          return (
            <TableCell key={key}>
              <Box>
                {value ? (
                  <Stack spacing={0.5}>
                    <Box
                      sx={{
                        maxHeight: needsTruncation ? '100px' : 'none',
                        overflow: 'hidden',
                        position: 'relative',
                        '&::after': needsTruncation ? {
                          content: '""',
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: '20px',
                          background: (theme) => `linear-gradient(to bottom, transparent 0%, ${theme.palette.background.paper} 100%)`,
                        } : {},
                      }}
                    >
                      <Box
                        sx={{ whiteSpace: "pre-wrap" }}
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(getUserDataValue(value))
                        }}
                      />
                    </Box>
                    {needsTruncation && (
                      <Button
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          openFullContentDialog(getUserDataValue(value), process.process_description);
                        }}
                        sx={{ alignSelf: "flex-start", minWidth: "auto", p: 0.5 }}
                      >
                        {getTranslation(translations, "generic.full_view")}
                      </Button>
                    )}
                  </Stack>
                ) : (
                  "N/A"
                )}
              </Box>
            </TableCell>
          );
        })}
        {!history && (
          <TableCell>
            <Stack>
              <span>
                <Typography>{task.flownodename}</Typography>
                {process.state === 1 && (
                  <FormHelperText>
                    {getTranslation(translations, "pm.process.started_at")}
                    {formatDate(process.startdoing)}
                  </FormHelperText>
                )}
                {process.state === 0 && (
                  <FormHelperText>
                    {getTranslation(translations, "pm.process.created")} {" "}
                    {formatDate(process.created)}
                  </FormHelperText>
                )}
              </span>
            </Stack>
          </TableCell>
        )}
        <TableCell>
          <Typography>
            {task.processduration
              ? formatDuration(task.processduration)
              : "N/A"}
          </Typography>
        </TableCell>
        {!history && (
          <TableCell>
            <Typography>
              {task.timetotakeover
                ? formatDuration(task.timetotakeover)
                : "N/A"}
            </Typography>
          </TableCell>
        )}
        {!history && (
          <TableCell>
            <Typography>
              {history && process.tasks && process.tasks.length > 0
                ? "-"
                : task.timeindoing
                  ? formatDuration(task.timeindoing)
                  : "N/A"}
            </Typography>
          </TableCell>
        )}
        {/* {(filters.priorities?.length || 0) > 0 && ( */}
        <TableCell onClick={(e) => e.stopPropagation()}>
          {isAdmin && !history ? (
            <PriorityMenu
              allPriorities={allPriorities}
              currentPriorityName={localPriorityName}
              loading={loadingPriorities}
              isEditing={isEditingPriority}
              onEditClick={() => setIsEditingPriority(true)}
              onPriorityChange={handlePriorityUpdate}
              onCancel={() => setIsEditingPriority(false)}
            />
          ) : (
            <Typography variant="body2">{localPriorityName || "N/A"}</Typography>
          )}
        </TableCell>
        {/* )} */}
        {!history && (
          <TableCell>
            <Typography>
              {task.state === 0
                ? getTranslation(translations, "pm.label.process_status.todo")
                : getTranslation(translations, "pm.label.process_status.doing")}
            </Typography>
          </TableCell>
        )}
        <TableCell>
          <Stack
            direction="row"
            gap={2}
            alignItems="center"
            onClick={(e) => {
              if (isEditingParticipant) {
                e.stopPropagation();
              }
            }}
          >
            {task.idtype === 3 ? (
              <Stack>
                <Stack direction="row">
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <ClockIcon />
                    <Typography>
                      {getTranslation(translations, "events.intermediate_catch")}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip
                      title={getTranslation(
                        translations,
                        "events.intermediate_catch.disclose"
                      )}
                    >
                      <IconButton
                        id="disclose-intermediate-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClick(task, "disclose");
                        }}
                        color="primary"
                      >
                        <LockOpenIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip
                      title={getTranslation(
                        translations,
                        "events.intermediate_catch.reschedule"
                      )}
                    >
                      <IconButton
                        id="disclose-intermediate-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClick(task, "reschedule");
                        }}
                        color="primary"
                      >
                        <CalendarMonthIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
                <Stack direction="column">
                  {isMonitorsLoading ? (
                    <CircularProgress />
                  ) : (
                    activeTimers.map((starttime) => (
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        key={starttime}
                      >
                        {new Date(starttime).toLocaleString()}
                      </Typography>
                    ))
                  )}
                </Stack>
              </Stack>
            ) : (
              <Stack direction="row" alignItems="center" gap={1}>
                {isAdmin && task.state !== 0 && (
                  <SubstitutesMenu
                    idOrganization={selectedOrganization?.id || 0}
                    idProcess={process.idprocess}
                    idToken={task.idprocesstoken}
                    currentUser={localUser.iduser ? {
                      id: localUser.iduser,
                      firstname: localUser.userfullname?.split(" ")[0] || "",
                      lastname: localUser.userfullname?.split(" ").slice(1).join(" ") || "",
                      picture: localUser.user_picture,
                    } : null}
                    isEditing={isEditingParticipant}
                    onEditClick={() => setIsEditingParticipant(true)}
                    onAssignmentChange={handleParticipantUpdate}
                    onlySelect
                    history={history}
                  />
                )}
                <MonitorParticipants
                  isAdmin={isAdmin}
                  iduser={localUser.iduser}
                  userfullname={localUser.userfullname}
                  user_picture={localUser.user_picture}
                  jsongroups={task.jsongroups}
                  jsonroles={task.jsonroles}
                />
              </Stack>
            )}
          </Stack>
        </TableCell>
        {!history && (
          <TableCell sx={{ borderRight: "1px solid #ddd", }}>
            <Stack direction="row" spacing={1}>
              <Tooltip title={getTranslation(translations, "tooltip.terminate.process")}>
                <span>
                  <IconButton
                    id="terminate-btn"
                    onClick={(e) => {
                      handleClick(task, "terminate");
                      e.stopPropagation();
                    }}
                    color="primary"
                    disabled={!isAdmin}
                  >
                    <BlockIcon />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title={getTranslation(translations, "tooltip.delete.process")}>
                <span>
                  <IconButton
                    id="delete-4ever-btn"
                    onClick={(e) => {
                      handleClick(task, "delete");
                      e.stopPropagation();
                    }}
                    color="primary"
                    disabled={!isAdmin}
                  >
                    <DeleteForever />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </TableCell>)}
        {history && (
          <TableCell>
            <Stack direction="row" spacing={0.5}>
              <IconButton
                id={`history-expand-button-${process.idprocess}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onExpandProcess?.();
                }}
                color="primary"
              >
                <ExpandMore />
              </IconButton>
            </Stack>
          </TableCell>
        )}
      </TableRow>
    </React.Fragment >
  );
});

export default TaskItem;
